import { FC, useState } from "react";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { Button } from "@/components/Base/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import {
  DropdownMenu as SortDropdown,
  DropdownMenuContent as SortDropdownContent,
  DropdownMenuTrigger as SortDropdownTrigger,
} from "@/components/Base/DropdownMenu";
import {
  DropdownMenu as ColumnDropdown,
  DropdownMenuContent as ColumnDropdownContent,
  DropdownMenuTrigger as ColumnDropdownTrigger,
} from "@/components/Base/DropdownMenu";
import { SortManagementPanel } from "./SortManagementPanel";
import { ColumnConfigurationPanel } from "./ColumnConfigurationPanel";
import { SortCriteria, ColumnConfig } from "../types";

interface ListViewControlsMenuProps {
  // Sort management props
  sortCriteria: SortCriteria[];
  onReorderSort: (fromIndex: number, toIndex: number) => void;
  onRemoveSort: (field: string) => void;
  onClearSort: () => void;
  hasSorting: boolean;

  // Column management props
  columns: ColumnConfig[];
  hiddenColumnIds: string[];
  columnOrder: string[];
  onUpdateColumnVisibility: (columnId: string, visible: boolean) => void;
  onReorderColumns: (newOrder: string[]) => void;
  onResetColumnsToDefaults: () => void;

  className?: string;
}

export const ListViewControlsMenu: FC<ListViewControlsMenuProps> = ({
  sortCriteria,
  onReorderSort,
  onRemoveSort,
  onClearSort,
  hasSorting,
  columns,
  hiddenColumnIds,
  columnOrder,
  onUpdateColumnVisibility,
  onReorderColumns,
  onResetColumnsToDefaults,
  className,
}) => {
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [columnPanelOpen, setColumnPanelOpen] = useState(false);

  const visibleColumnsCount = columns.filter(
    (col) => col.visible && !hiddenColumnIds.includes(col.id)
  ).length;
  const totalColumnsCount = columns.length;

  const hasColumnChanges =
    hiddenColumnIds.length > 0 ||
    JSON.stringify(columnOrder) !== JSON.stringify(columns.map((c) => c.id));

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1 text-xs font-medium"
          >
            <Icon name={IconNames.settings} size={14} />
            View Settings
            {(hasSorting || hasColumnChanges) && (
              <div className="ml-1 h-2 w-2 rounded-full bg-primary" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* Sort Order Option */}
          <SortDropdown open={sortPanelOpen} onOpenChange={setSortPanelOpen}>
            <SortDropdownTrigger asChild>
              <DropdownMenuItem
                className="flex cursor-pointer items-center justify-between"
                onSelect={(e) => e.preventDefault()}
              >
                <div className="flex items-center gap-2">
                  <Icon name={IconNames.sort} size={14} />
                  <span>Sort Order</span>
                </div>
                <div className="flex items-center gap-1">
                  {hasSorting && (
                    <>
                      <span className="text-xs text-muted-foreground">
                        {sortCriteria.length} level
                        {sortCriteria.length !== 1 ? "s" : ""}
                      </span>
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </>
                  )}
                  <Icon name={IconNames.chevronRight} size={12} />
                </div>
              </DropdownMenuItem>
            </SortDropdownTrigger>
            <SortDropdownContent
              side="right"
              sideOffset={5}
              className="w-96 max-w-none p-0"
            >
              {hasSorting ? (
                <SortManagementPanel
                  sortCriteria={sortCriteria}
                  columns={columns}
                  onReorder={onReorderSort}
                  onRemove={onRemoveSort}
                  onClear={onClearSort}
                  className="border-0 bg-transparent"
                />
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No sorting applied. Click column headers to add sort criteria.
                </div>
              )}
            </SortDropdownContent>
          </SortDropdown>

          <DropdownMenuSeparator />

          {/* Column Configuration Option */}
          <ColumnDropdown
            open={columnPanelOpen}
            onOpenChange={setColumnPanelOpen}
          >
            <ColumnDropdownTrigger asChild>
              <DropdownMenuItem
                className="flex cursor-pointer items-center justify-between"
                onSelect={(e) => e.preventDefault()}
              >
                <div className="flex items-center gap-2">
                  <Icon name={IconNames.layouts} size={14} />
                  <span>Column Configuration</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">
                    {visibleColumnsCount}/{totalColumnsCount}
                  </span>
                  {hasColumnChanges && (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  )}
                  <Icon name={IconNames.chevronRight} size={12} />
                </div>
              </DropdownMenuItem>
            </ColumnDropdownTrigger>
            <ColumnDropdownContent
              side="right"
              sideOffset={5}
              className="w-96 max-w-none p-0"
            >
              <ColumnConfigurationPanel
                columns={columns}
                hiddenColumnIds={hiddenColumnIds}
                columnOrder={columnOrder}
                onUpdateVisibility={onUpdateColumnVisibility}
                onReorderColumns={onReorderColumns}
                onResetToDefaults={onResetColumnsToDefaults}
              />
            </ColumnDropdownContent>
          </ColumnDropdown>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
